import taxonomyData from '../data/diagramloom_taxonomy.json';

// Template renderer - simple placeholder replacement
function renderTemplate(template, data) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return data[key] || 'TBD';
  });
}

// Get artifact meaning from taxonomy
function getArtifactMeaning(artifactId) {
  const artifact = taxonomyData.find(item => item.acronym === artifactId);
  return artifact ? artifact.meaning : '';
}

// PRD Template
const PRD_TEMPLATE = `# {{projectName}} — Product Requirements Document (PRD)

> **Purpose:** {{artifactMeaning}}

## 1. Overview
**Project Name:** {{projectName}}  
**Description:** {{projectDescription}}  
**Owner:** {{projectOwner}}  
**Date:** {{currentDate}}

## 2. Goals & Objectives
{{goalsAndObjectives}}

## 3. Scope
**In Scope:**
- {{inScopeItems}}

**Out of Scope:**
- {{outOfScopeItems}}

## 4. Functional Requirements
{{functionalRequirements}}

## 5. Non-Functional Requirements (NFRs)
{{nonFunctionalRequirements}}

## 6. Acceptance Criteria
{{acceptanceCriteria}}

## 7. Success Metrics
{{successMetrics}}

## 8. Stakeholders & Roles
{{stakeholders}}

## 9. Dependencies & Constraints
{{dependenciesConstraints}}

---
*Generated by DiagramLoom using the {{presetTier}} preset.*`;

// C4 Context Template
const C4_CONTEXT_TEMPLATE = `@startuml
' C4-PlantUML Setup
!include https://raw.githubusercontent.com/plantuml-stdlib/C4-PlantUML/master/C4_Context.puml

title {{projectName}} - C4 Context Diagram
caption {{artifactMeaning}}

' Person / Roles
Person(user, "{{primaryActorName}}", "{{primaryActorDescription}}")

' System under design
System(system, "{{projectName}}", "{{systemDescription}}")

' External Systems
System_Ext(ext1, "{{externalSystem1Name}}", "{{externalSystem1Description}}")
System_Ext(ext2, "{{externalSystem2Name}}", "{{externalSystem2Description}}")

' Relationships
Rel(user, system, "{{relationshipUserToSystem}}")
Rel(system, ext1, "{{relationshipSystemToExt1}}")
Rel(system, ext2, "{{relationshipSystemToExt2}}")

SHOW_LEGEND()
@enduml`;

// Data extraction from project brief
function extractPRDData(projectBrief, presetTier) {
  return {
    artifactMeaning: getArtifactMeaning('PRD'),
    projectName: projectBrief.projectName || 'ProphetPlay.ai',
    projectDescription: projectBrief.description || 'A high-volume, real-time prediction platform for sports, entertainment, and financial events without real money wagering.',
    projectOwner: 'Product Manager',
    currentDate: new Date().toLocaleDateString(),
    goalsAndObjectives: `
- Provide real-time prediction capabilities for sports and entertainment events
- Support high-volume concurrent users with low latency
- Ensure no real money wagering compliance
- Scale to handle peak event traffic`,
    inScopeItems: `
- Real-time prediction engine
- User authentication and profiles
- Event data integration
- Prediction tracking and scoring
- Leaderboards and social features`,
    outOfScopeItems: `
- Real money transactions
- Gambling features
- Third-party payment processing
- Mobile native applications (Phase 1)`,
    functionalRequirements: `
- **User Management**: Registration, authentication, profile management
- **Event Integration**: Real-time sports/entertainment data feeds
- **Prediction Engine**: Submit predictions, calculate scores, track accuracy
- **Social Features**: Leaderboards, user rankings, prediction sharing
- **Analytics**: User behavior tracking, prediction performance metrics`,
    nonFunctionalRequirements: `
- **Availability**: ${projectBrief.availability || '99.9% uptime'}
- **Performance**: Sub-200ms response time for predictions
- **Scalability**: Support 10,000+ concurrent users during peak events
- **Security**: ${projectBrief.authentication || 'OAuth 2.0'} authentication
- **Data**: ${projectBrief.dataTypes || 'User profiles and prediction data'}`,
    acceptanceCriteria: `
- Users can register and authenticate successfully
- Real-time event data displays within 5 seconds of updates
- Predictions can be submitted up to event start time
- Scoring calculations complete within 1 hour of event conclusion
- System maintains performance during peak traffic`,
    successMetrics: `
- Daily Active Users (DAU) > 1,000
- Prediction accuracy tracking > 95% uptime
- Average session duration > 10 minutes
- User retention rate > 60% after 30 days`,
    stakeholders: `
- **Product Manager**: Requirements and roadmap ownership
- **Engineering Lead**: Technical implementation
- **UX Designer**: User experience design
- **Data Engineer**: Event feed integration`,
    dependenciesConstraints: `
- External sports/entertainment data providers
- Cloud infrastructure scaling capabilities
- Compliance with gaming regulations (no real money)
- ${projectBrief.deployment || 'Cloud-based deployment strategy'}`,
    presetTier: presetTier
  };
}

function extractC4ContextData(projectBrief, presetTier) {
  return {
    artifactMeaning: getArtifactMeaning('C4-CTX'),
    projectName: projectBrief.projectName || 'ProphetPlay.ai',
    systemDescription: projectBrief.description || 'Real-time prediction platform',
    primaryActorName: 'Users',
    primaryActorDescription: 'Sports and entertainment fans making predictions',
    externalSystem1Name: 'Sports Data API',
    externalSystem1Description: 'Real-time sports and entertainment event data',
    externalSystem2Name: 'Authentication Service',
    externalSystem2Description: 'User authentication and profile management',
    relationshipUserToSystem: 'Makes predictions, views leaderboards',
    relationshipSystemToExt1: 'Fetches real-time event data',
    relationshipSystemToExt2: 'Authenticates users, manages profiles'
  };
}

// Main generation function
export function generateArtifact(artifactId, projectBrief, presetTier = 'Starter') {
  switch (artifactId) {
    case 'PRD':
      const prdData = extractPRDData(projectBrief, presetTier);
      return {
        id: artifactId,
        name: 'Product Requirements Document',
        content: renderTemplate(PRD_TEMPLATE, prdData),
        format: 'markdown',
        filename: `${prdData.projectName.replace(/\s+/g, '_')}_PRD.md`
      };
      
    case 'C4-CTX':
      const c4Data = extractC4ContextData(projectBrief, presetTier);
      return {
        id: artifactId,
        name: 'C4 Context Diagram',
        content: renderTemplate(C4_CONTEXT_TEMPLATE, c4Data),
        format: 'plantuml',
        filename: `${c4Data.projectName.replace(/\s+/g, '_')}_C4_Context.puml`
      };
      
    default:
      throw new Error(`Unsupported artifact: ${artifactId}`);
  }
}

// Generate placeholder for unsupported artifacts
function generatePlaceholder(artifactId, projectBrief) {
  const artifact = taxonomyData.find(item => item.acronym === artifactId);
  const name = artifact ? artifact.name : 'Unknown Artifact';
  const meaning = artifact ? artifact.meaning : 'Artifact description not available';
  
  return {
    id: artifactId,
    name: name,
    content: `# ${name}

> **Purpose:** ${meaning}

## Coming Soon

This artifact template is not yet implemented. The generator currently supports:
- PRD (Product Requirements Document)
- C4-CTX (C4 Context Diagram)

Additional templates will be added in future releases.

---
*Generated by DiagramLoom - Template placeholder*`,
    format: 'markdown',
    filename: `${artifactId}_placeholder.md`,
    isPlaceholder: true
  };
}

// Generate multiple artifacts
export function generateArtifacts(artifactIds, projectBrief, presetTier = 'Starter') {
  return artifactIds.map(id => {
    try {
      return generateArtifact(id, projectBrief, presetTier);
    } catch (error) {
      // Generate placeholder for unsupported artifacts
      return generatePlaceholder(id, projectBrief);
    }
  });
}